import { GridSortModel } from '@mui/x-data-grid';
import { generatePath } from 'react-router-dom';
import { ProjectsPageOptions } from 'Types/dealGPT';

import { myFolder } from '../redux/consts';
import {
  AppEnum,
  Commentable,
  ConversationType,
  DealGPTDiscoveryView,
  DealGPTQuestionStatus,
  EventSource,
} from '../Types/enums';
import { UserRequest } from '../Types/userRequest';
import {
  AdminProjectsPath,
  AnalysisScopesPath,
  CategoriesPath,
  CategoryPath,
  CollectionCreatorsPath,
  CollectionPath,
  CollectionsPath,
  CollectionsUploadPath,
  CollectionTagsPath,
  CommentPath,
  CommentsPath,
  ConflictReportPath,
  ConflictReportValidationPath,
  ConversationsPath,
  CreateKpiQuestionPath,
  CreateProjectRunPath,
  DataRequestItem,
  DataRequestItemQuestions,
  DataRequestList,
  DataRequestListAll,
  DataRequestListBulk,
  DataRequestListReIndex,
  DeleteKeyQuestionPath,
  DeleteKpiQuestionPath,
  DiscoveryScopesPath,
  EventsPath,
  fetchCategoryQuestionsPath,
  fetchQuestionAllVersionsPath,
  FoldersPath,
  GenerateAnalysisReportPath,
  GenerateDiscoveryReportPath,
  GenerateRecommendationPromptPath,
  GraphRagVisualizationPath,
  IndexPath,
  InvestmentThesisPath,
  KeyQuestionPath,
  KeyQuestionRelevantDocsPath,
  KpiQuestionVersionsPath,
  LatestProjectRunPath,
  NotificationPath,
  NotificationsPath,
  ProjectConfigPath,
  ProjectConfigStatusPath,
  ProjectDataProcessPath,
  ProjectDefaultKPIsPath,
  ProjectKpisRunPath,
  ProjectPath,
  ProjectQuestionRescanPath,
  ProjectQuestionRunInclusionPath,
  ProjectRunPath,
  ProjectRunProgressPath,
  ProjectRunScopesPath,
  ProjectRunStatusesPath,
  ProjectSalesforceInfo,
  ProjectSemaRunPath,
  ProjectsPath,
  PromptLabelDefinitionsPath,
  PromptLabelsPath,
  PromptSuggestionsPath,
  PromptTypePath,
  PromptTypesPath,
  PromptVersionsPath,
  QuestionPath,
  QuestionsPath,
  RecalculateBenchmarksPath,
  ReorderCategoriesPath,
  ReorderQuestionsPath,
  SOWExtractionPath,
  StartProjectRunPath,
  SummaryChatAssistantPath,
  SummaryChatFilesAssistantPath,
  SummaryChatFilesPath,
  TeamsFolderExistsPath,
  UserRequestsPath,
  UsersPath,
} from './routesDef';

export const entityCommentsPathUrl = (entityType: Commentable, entityId: string) => {
  const queryParams = new URLSearchParams();
  queryParams.append('entityType', entityType);
  queryParams.append('entityId', entityId);

  return `${generatePath(CommentsPath)}?${queryParams.toString()}`;
};

export const createCommentsPathUrl = () => generatePath(CommentsPath);

export const commentPathUrl = (commentId: string) => generatePath(CommentPath, { commentId });

export const usersPathUrl = (ids: string[], search: string, page?: number, limit?: number) => {
  const queryParams = new URLSearchParams();
  ids.forEach((id) => queryParams.append('ids', id));
  queryParams.append('search', search);
  queryParams.append('limit', limit?.toString() ?? '10');
  queryParams.append('page', page?.toString() ?? '0');

  return `${generatePath(UsersPath)}?${queryParams.toString()}`;
};

export const discoveryScopesPathUrl = (projectType: string) => {
  const queryParams = new URLSearchParams();
  queryParams.append('projectType', projectType);

  return `${generatePath(DiscoveryScopesPath)}?${queryParams.toString()}`;
};

export const analysisScopesPathUrl = (projectType: string, projectId: string) => {
  const queryParams = new URLSearchParams();
  queryParams.append('projectType', projectType);
  queryParams.append('projectId', projectId);

  return `${generatePath(AnalysisScopesPath)}?${queryParams.toString()}`;
};

export const collectionCreatorsPathUrl = () => generatePath(CollectionCreatorsPath);

export const collectionTagsPathUrl = () => generatePath(CollectionTagsPath);

export const collectionsPathUrl = ({
  ids = [],
  owners = [],
  visibility = [],
  tags = [],
  shared = false,
  excludeEmpty = false,
}: {
  ids?: string[];
  owners?: string[];
  visibility?: string[];
  tags?: string[];
  shared?: boolean;
  excludeEmpty?: boolean;
}) => {
  const queryParams = new URLSearchParams();

  ids.forEach((id) => queryParams.append('ids', id));
  visibility.forEach((v) => queryParams.append('visibility', v));
  owners.forEach((o) => queryParams.append('owners', o));
  tags.forEach((t) => queryParams.append('tags', t));
  queryParams.append('shared', shared.toString());
  queryParams.append('excludeEmpty', excludeEmpty.toString());

  return `${generatePath(CollectionsPath)}?${queryParams.toString()}`;
};

export const createCollectionPathUrl = () => generatePath(CollectionsPath);

export const collectionUploadPathUrl = () => generatePath(CollectionsUploadPath);

export const collectionPathUrl = (collectionId: string) =>
  generatePath(CollectionPath, { collectionId });

export const createQuestionPathUrl = () => generatePath(QuestionsPath);

export const questionPathUrl = (questionId: string) => generatePath(QuestionPath, { questionId });

export const reorderQuestionsPathUrl = () => generatePath(ReorderQuestionsPath);

export const createCategoryPathUrl = () => generatePath(CategoriesPath);

export const categoryPathUrl = (categoryId: string) => generatePath(CategoryPath, { categoryId });

export const collectionCategoriesPathUrl = (collectionId: string) => {
  const searchParams = new URLSearchParams();
  searchParams.append('collectionId', collectionId);
  return `${generatePath(CategoriesPath)}?${searchParams.toString()}`;
};

export const categoryQuestionsPathUrl = (categoryId: string) => {
  const searchParams = new URLSearchParams();
  searchParams.append('categoryId', categoryId);
  return `${generatePath(QuestionsPath)}?${searchParams.toString()}`;
};

export const reorderCategoriesPathUrl = () => generatePath(ReorderCategoriesPath);

export const projectsPathUrl = (options: ProjectsPageOptions) => {
  const {
    page,
    pageSize,
    showArchived,
    searchQuery,
    sortField,
    sortDirection,
    filterModel,
    adminShowAll,
  } = options;

  const searchParams = new URLSearchParams();
  searchParams.append('page', page.toString());
  searchParams.append('pageSize', pageSize.toString());
  searchParams.append('showArchived', showArchived.toString());

  if (searchQuery?.trim()) searchParams.append('searchQuery', searchQuery.trim());

  if (sortField) searchParams.append('sortField', sortField);

  if (sortDirection) searchParams.append('sortDirection', sortDirection);

  if (filterModel) searchParams.append('filterModel', JSON.stringify(filterModel));

  return `${generatePath(
    adminShowAll ? AdminProjectsPath : ProjectsPath
  )}?${searchParams.toString()}`;
};

export const projectPathUrl = (projectId: string, isAdmin?: boolean) => {
  const searchParams = new URLSearchParams();
  if (isAdmin !== undefined) searchParams.append('isAdmin', isAdmin.toString());
  return `${generatePath(ProjectPath, { projectId })}?${searchParams.toString()}`;
};

export const projectDefaultKPIsPathUrl = (projectId: string) => {
  return generatePath(ProjectDefaultKPIsPath, { projectId });
};

export const projectFolderExistsPathUrl = (
  teamsId: string,
  channelId?: string,
  sharepointFolderURL?: string
) => {
  const searchParams = new URLSearchParams();
  searchParams.append('teamsId', teamsId);
  if (channelId) searchParams.append('channelId', channelId);
  if (sharepointFolderURL) searchParams.append('sharepointFolderURL', sharepointFolderURL);
  return `${generatePath(TeamsFolderExistsPath)}?${searchParams.toString()}`;
};

export const createProjectRunPathUrl = (projectId: string) => {
  return generatePath(CreateProjectRunPath, { projectId });
};

export const getLatestProjectRunPathUrl = (projectId: string, runType: string) => {
  const queryParams = new URLSearchParams();
  queryParams.append('runType', runType);
  return `${generatePath(LatestProjectRunPath, { projectId })}?${queryParams.toString()}`;
};

export const updateProjectRunPathUrl = (projectId: string, runId: string) => {
  return generatePath(ProjectRunPath, { projectId, runId });
};

export const startProjectRunPathUrl = (projectId: string, runId: string) => {
  return generatePath(StartProjectRunPath, { projectId, runId });
};

export const projectRunProgressPathUrl = (
  projectId: string,
  runId: string,
  scopes: string[] = [],
  statuses: DealGPTQuestionStatus[] = [],
  searchQuery?: string
) => {
  // Append each value of the arrays separately
  const queryParams = new URLSearchParams();
  scopes.forEach((scope) => queryParams.append('scopes', scope));
  statuses.forEach((status) => queryParams.append('statuses', status));

  if (searchQuery) queryParams.append('searchQuery', searchQuery);

  return `${generatePath(ProjectRunProgressPath, { projectId, runId })}?${queryParams.toString()}`;
};

export const projectRunScopesPathUrl = (projectId: string, runId: string) =>
  generatePath(ProjectRunScopesPath, { projectId, runId });

export const projectRunStatusesPathUrl = (projectId: string, runId: string) =>
  generatePath(ProjectRunStatusesPath, { projectId, runId });

export const projectConfigPathUrl = (projectId: string) =>
  generatePath(ProjectConfigPath, { projectId });

export const projectConfigStatusPathUrl = (projectId: string) =>
  generatePath(ProjectConfigStatusPath, { projectId });

export const projectSalesforceInfoPathUrl = (projectId: string) =>
  generatePath(ProjectSalesforceInfo, { projectId });

export const projectDataProcessPathUrl = (projectId: string) => {
  return generatePath(ProjectDataProcessPath, { projectId });
};

export const projectKpisRunPathUrl = (projectId: string, runId: string) =>
  generatePath(ProjectKpisRunPath, { projectId, runId });

export const projectSemaRunPathUrl = (projectId: string) =>
  generatePath(ProjectSemaRunPath, { projectId });

export const investmentThesisPathUrl = (projectId: string) =>
  generatePath(InvestmentThesisPath, { projectId });

export const createKpiQuestionPathUrl = (projectId: string) =>
  generatePath(CreateKpiQuestionPath, { projectId });

export const deleteKpiQuestionPathUrl = (projectId: string, questionId: string) =>
  generatePath(DeleteKpiQuestionPath, { projectId, questionId });

export const kpiQuestionVersionsPathUrl = (projectId: string, questionId: string) =>
  generatePath(KpiQuestionVersionsPath, { projectId, questionId });

export const keyQuestionPathUrl = (projectId: string) =>
  generatePath(KeyQuestionPath, { projectId });

export const deleteKeyQuestionPathUrl = (projectId: string, questionId: string) =>
  generatePath(DeleteKeyQuestionPath, { projectId, questionId });

export const keyQuestionRelevantDocsPathUrl = (
  projectId: string,
  questionId: string,
  runId: string
) => {
  const queryParams = new URLSearchParams();
  queryParams.append('runId', runId);
  return `${generatePath(KeyQuestionRelevantDocsPath, {
    projectId,
    questionId,
  })}?${queryParams.toString()}`;
};

export const fetchQuestionAllVersionsPathUrl = (
  questionId: string,
  statuses: DealGPTQuestionStatus[]
) => {
  const queryParams = new URLSearchParams();
  statuses.forEach((status) => queryParams.append('statuses', status));

  return `${generatePath(fetchQuestionAllVersionsPath, { questionId })}?${queryParams.toString()}`;
};

export const projectQuestionRunInclusionPathUrl = (questionId: string) => {
  return generatePath(ProjectQuestionRunInclusionPath, {
    questionId,
  });
};

export const projectQuestionRescanPathUrl = () => generatePath(ProjectQuestionRescanPath);

export const fetchCategoryQuestionsPathUrl = (
  projectId: string,
  scopeName: string,
  categoryName: string,
  statuses: DealGPTQuestionStatus[],
  searchQuery: string,
  runId: string
) => {
  const queryParams = new URLSearchParams();
  statuses.forEach((status) => queryParams.append('statuses', status));
  if (searchQuery) {
    queryParams.append('searchQuery', searchQuery);
  }

  return `${generatePath(fetchCategoryQuestionsPath, {
    projectId,
    scopeName,
    categoryName,
    runId,
  })}?${queryParams.toString()}`;
};

export const generateDiscoveryReportPathUrl = (
  projectId: string,
  reportType: DealGPTDiscoveryView,
  scopes: string[],
  statuses: DealGPTQuestionStatus[],
  selectedRunId?: string
): string => {
  const queryParams = new URLSearchParams();
  queryParams.append('reportType', reportType);
  scopes.forEach((scope) => queryParams.append('scopes', scope));
  statuses.forEach((status) => queryParams.append('statuses', status));
  if (selectedRunId) {
    queryParams.append('selectedRunId', selectedRunId);
  }
  return `${generatePath(GenerateDiscoveryReportPath, { projectId })}?${queryParams.toString()}`;
};

export const generateAnalysisReportPathUrl = (
  projectId: string,
  scope: string,
  runId: string
): string => {
  return `${generatePath(GenerateAnalysisReportPath, { projectId, scope, runId })}`;
};

export const recalculateBenchmarksPathUrl = (projectId: string, runId: string): string => {
  return `${generatePath(RecalculateBenchmarksPath, { projectId, runId })}`;
};

export const sowExtractionPathUrl = (projectId: string) => {
  return generatePath(SOWExtractionPath, { projectId });
};

export const promptVersionsPathUrl = (promptId: string) => {
  return generatePath(PromptVersionsPath, { promptId });
};

export const promptSuggestionsPathUrl = () => {
  return generatePath(PromptSuggestionsPath);
};

export const conflictReportValidationPathUrl = (projectId: string) => {
  return generatePath(ConflictReportValidationPath, { projectId });
};

export const conflictReportPathUrl = (projectId: string) => {
  return generatePath(ConflictReportPath, { projectId });
};

export const generateRecommendationPromptPathUrl = () =>
  generatePath(GenerateRecommendationPromptPath);

export const PromptLabelDefinitionsPathUrl = () => generatePath(PromptLabelDefinitionsPath);

export const PromptLabelsPathUrl = () => generatePath(PromptLabelsPath);

export const PromptTypesPathUrl = () => generatePath(PromptTypesPath);

export const PromptTypePathUrl = (promptTypeId: string) => {
  return generatePath(PromptTypePath, { promptTypeId });
};

export const userRequestsPathUrl = (
  limit: number,
  page: number,
  status?: UserRequest['status']
) => {
  const offset = (page - 1) * limit;
  const queryParams = new URLSearchParams();
  queryParams.append('limit', limit.toString());
  queryParams.append('offset', offset.toString());
  if (status) {
    queryParams.append('status', status);
  }
  return `${generatePath(UserRequestsPath)}?${queryParams.toString()}`;
};

export const notificationsPathUrl = (userId: string) => {
  const queryParams = new URLSearchParams();
  queryParams.append('userId', userId);

  return `${generatePath(NotificationsPath)}?${queryParams.toString()}`;
};

export const notificationPathUrl = (notificationId: string) => {
  return generatePath(NotificationPath, { notificationId });
};

export const latestIndexRunPathUrl = (indexId: string) => {
  if (!indexId) {
    throw new Error('Index ID is required to generate the latest index run URL');
  }
  return `${generatePath(`${IndexPath}/:indexId/index-run/latest`, {
    indexId,
  })}`;
};

export const dataRequestListPathUrl = (
  projectId: string,
  scopes: string[],
  sortModel: GridSortModel
) => {
  const searchParams = new URLSearchParams();
  scopes.forEach((scope) => searchParams.append('scope', scope));
  const sortItem = sortModel[0];
  if (sortItem) searchParams.append('sortField', sortItem.field);
  if (sortItem?.sort) searchParams.append('sortDirection', sortItem.sort);

  return `${generatePath(DataRequestList, { projectId })}?${searchParams.toString()}`;
};

export const dataRequestListAllPathUrl = (projectId: string) => {
  return generatePath(DataRequestListAll, { projectId });
};

export const dataRequestListBulkPathUrl = (projectId: string) => {
  return generatePath(DataRequestListBulk, { projectId });
};

export const dataRequestListReIndexPathUrl = (projectId: string, indexType: string) => {
  return generatePath(DataRequestListReIndex, { projectId, indexType });
};

export const generateDataRequestListReportPathUrl = (
  projectId: string,
  scopes: string[],
  sortModel: GridSortModel
) => {
  const searchParams = new URLSearchParams();
  scopes.forEach((scope) => searchParams.append('scope', scope));
  const sortItem = sortModel[0];
  if (sortItem) searchParams.append('sortField', sortItem.field);
  if (sortItem?.sort) searchParams.append('sortDirection', sortItem.sort);

  return `${generatePath(`${DataRequestList}/report`, { projectId })}?${searchParams.toString()}`;
};

export const dataRequestItemPathUrl = (projectId: string, itemId: string) => {
  return generatePath(DataRequestItem, { projectId, itemId });
};

export const dataRequestItemQuestionsPathUrl = (projectId: string, itemId: string) => {
  return generatePath(DataRequestItemQuestions, { projectId, itemId });
};

export const projectEventsPathUrl = (
  entityType: EventSource,
  entityId: string,
  projectId?: string
) => {
  const searchParams = new URLSearchParams();
  searchParams.append('entityType', entityType);
  searchParams.append('entityId', entityId);

  if (projectId) {
    searchParams.append('projectId', projectId);
  }

  return `${generatePath(EventsPath)}?${searchParams.toString()}`;
};

export const summaryChatPathUrl = (projectId: string) => {
  return generatePath(SummaryChatAssistantPath, { projectId });
};

export const summaryChatFilesAssistantPathUrl = (vectorStoreId: string) => {
  return generatePath(SummaryChatFilesAssistantPath, { vectorStoreId });
};

export const summaryChatFilesPathUrl = (
  projectId: string,
  ids?: string[],
  collectionId?: string,
  scope?: string,
  page?: string
) => {
  const searchParams = new URLSearchParams();
  if (ids) ids.forEach((id) => searchParams.append('ids', id));
  if (collectionId) searchParams.append('collectionId', collectionId);
  if (scope) searchParams.append('scope', scope);
  if (page) searchParams.append('page', page);

  return `${generatePath(SummaryChatFilesPath, { projectId })}?${searchParams.toString()}`;
};

export const projectFoldersPathUrl = ({
  foldersWithProjects,
  limit,
  offset,
  q,
  dealgptReturnAll,
  showArchived,
  page,
  pageSize,
  sortField,
  sortDirection,
  filterModel,
  projectSearchQuery,
}: {
  foldersWithProjects?: boolean;
  limit?: number;
  offset?: number;
  q?: string;
  dealgptReturnAll?: boolean;
  showArchived?: boolean;
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  filterModel?: string; // JSON string
  projectSearchQuery?: string;
}) => {
  const searchParams = new URLSearchParams();
  if (foldersWithProjects !== undefined)
    searchParams.append('foldersWithProjects', foldersWithProjects.toString());
  if (limit !== undefined) searchParams.append('limit', limit.toString());
  if (offset !== undefined) searchParams.append('offset', offset.toString());
  if (q) searchParams.append('q', q);
  if (dealgptReturnAll !== undefined)
    searchParams.append('dealgptReturnAll', String(dealgptReturnAll));
  if (showArchived !== undefined) searchParams.append('showArchived', String(showArchived));
  if (page !== undefined) searchParams.append('page', page.toString());
  if (pageSize !== undefined) searchParams.append('pageSize', pageSize.toString());
  if (sortField) searchParams.append('sortField', sortField);
  if (sortDirection) searchParams.append('sortDirection', sortDirection);
  if (filterModel) searchParams.append('filterModel', filterModel);
  if (projectSearchQuery) searchParams.append('searchQuery', projectSearchQuery);

  return `${generatePath(FoldersPath)}?${searchParams.toString()}`;
};

export const conversationsPathUrl = (
  selectedApp: AppEnum,
  searchQuery?: string,
  pinned?: boolean,
  folderId?: string,
  page?: number,
  pageSize?: number,
  type?: ConversationType
) => {
  const queryParams = new URLSearchParams();

  if (selectedApp === AppEnum.DEAL_GPT) {
    queryParams.append('conversationsWithProjects', 'true');
  }

  if (searchQuery && searchQuery.trim().length > 0) {
    queryParams.append('q', searchQuery.trim());
  }

  if (pinned) {
    queryParams.append('pinnedOnly', 'true');
  } else if (folderId === myFolder.id) {
    queryParams.append('folderId', 'null');
  } else if (folderId) {
    queryParams.append('folderId', folderId);
  }

  if (type) {
    queryParams.append('type', type);
  }

  if (page !== undefined) queryParams.append('page', page.toString());
  if (pageSize !== undefined) queryParams.append('pageSize', pageSize.toString());

  return `${generatePath(ConversationsPath)}?${queryParams.toString()}`;
};

export const graphRagVisualizationPathUrl = (graphIndexId: string) => {
  return generatePath(GraphRagVisualizationPath, { graphIndexId });
};
